import { useTranslation } from 'react-i18next';

import { Select } from '@/app/components/ui/Field';
import type { DashboardRange, RelativeRange } from '@/features/statistics/api';

const PRESETS: RelativeRange[] = ['last_7_days', 'last_30_days', 'this_week', 'this_month'];
const NINETY_DAYS = '90d';
const CUSTOM = 'custom';

function presetLabelKey(preset: RelativeRange) {
  switch (preset) {
    case 'last_7_days':
      return 'statistics.range.last7Days' as const;
    case 'last_30_days':
      return 'statistics.range.last30Days' as const;
    case 'this_week':
      return 'statistics.range.thisWeek' as const;
    case 'this_month':
      return 'statistics.range.thisMonth' as const;
  }
}

function ninetyDaysRange(): { from: string; to: string } {
  const to = new Date().toISOString().slice(0, 10);
  const from = new Date(Date.now() - 89 * 86_400_000).toISOString().slice(0, 10);
  return { from, to };
}

function isNinetyDays(range: DashboardRange): boolean {
  if (typeof range === 'string') return false;
  const expected = ninetyDaysRange();
  // Same day-count and "ending today" as `ninetyDaysRange()` produces —
  // exact date match would drift a day stale between page-load and re-render.
  const days = Math.round(
    (new Date(`${range.to}T00:00:00Z`).getTime() - new Date(`${range.from}T00:00:00Z`).getTime()) /
      86_400_000,
  );
  return days === 89 && range.to === expected.to;
}

/**
 * The same period presets `RangePicker` offers, plus "Laatste 90 dagen" (an
 * explicit `{from, to}` computed client-side — no new relative-range keyword
 * for the API to support, see `ninetyDaysRange()`), as one dropdown instead
 * of a button row — Vitaliteit/Deelname's filter row reads as a set of
 * dropdowns, unlike the button-style `RangePicker` other statistics pages
 * already use and that this intentionally leaves untouched.
 */
export function RangeSelect({
  value,
  onChange,
  className,
}: {
  value: DashboardRange;
  onChange: (range: DashboardRange) => void;
  className?: string;
}) {
  const { t } = useTranslation();

  const selectValue = PRESETS.includes(value as RelativeRange)
    ? (value as RelativeRange)
    : isNinetyDays(value)
      ? NINETY_DAYS
      : CUSTOM;

  return (
    <Select
      className={className}
      value={selectValue}
      onChange={(e) => {
        const next = e.target.value;
        if (next === NINETY_DAYS) {
          onChange(ninetyDaysRange());
          return;
        }
        if (next === CUSTOM) {
          const to = new Date().toISOString().slice(0, 10);
          const from = new Date(Date.now() - 29 * 86_400_000).toISOString().slice(0, 10);
          onChange({ from, to });
          return;
        }
        onChange(next as RelativeRange);
      }}
    >
      {PRESETS.map((preset) => (
        <option key={preset} value={preset}>
          {t(presetLabelKey(preset))}
        </option>
      ))}
      <option value={NINETY_DAYS}>{t('statistics.range.last90Days')}</option>
      <option value={CUSTOM}>{t('statistics.range.custom')}</option>
    </Select>
  );
}
